import { cn, NoddiIcon } from 'noddi-ui-common';
import {
  ALL_DURATION_UNITS,
  buildDurationSeconds,
  clampDurationSeconds,
  commonTexts,
  reduceDurationParts,
  type DurationPreset,
  type DurationTranslations,
  type DurationUnit
} from 'noddi-util';
import { useId, useState } from 'react';
import { Button } from '../../../base/button/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../Overlay/NoddiPopover';
import { NoddiTooltip } from '../../Overlay/NoddiTooltip';
import { DurationReadout } from './DurationReadout';
import { DurationUnitStepper } from './DurationUnitStepper';

export type NoddiDurationPickerProps = {
  value: number;
  onChange: (seconds: number) => void;
  units?: DurationUnit[];
  presets?: DurationPreset[];
  min?: number;
  max?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  className?: string;
  id?: string;
  translations?: Partial<DurationTranslations>;
};

const UNIT_LONG_LABELS: Record<
  DurationUnit,
  keyof Pick<DurationTranslations, 'unitDayLong' | 'unitHourLong' | 'unitMinuteLong' | 'unitSecondLong'>
> = {
  d: 'unitDayLong',
  h: 'unitHourLong',
  m: 'unitMinuteLong',
  s: 'unitSecondLong'
};

export const NoddiDurationPicker = ({
  value,
  onChange,
  units = ALL_DURATION_UNITS as DurationUnit[],
  presets,
  min = 0,
  max = Infinity,
  disabled = false,
  autoFocus = false,
  label,
  tooltip,
  placeholder,
  className,
  id,
  translations: customTranslations
}: NoddiDurationPickerProps) => {
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const labelId = `${triggerId}_label`;

  const translations = { ...commonTexts.duration, ...customTranslations };

  const set = (n: number) => onChange(clampDurationSeconds(n, min, max));

  const visibleUnits = ALL_DURATION_UNITS.filter((u) => units.includes(u));
  const reduced = reduceDurationParts(value || 0, visibleUnits);

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {label && (
        <div className='flex items-center gap-2'>
          <label id={labelId} htmlFor={triggerId} className='text-sm'>
            {label}
          </label>
          {tooltip && <NoddiTooltip content={tooltip} />}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            id={triggerId}
            type='button'
            variant='outline'
            disabled={disabled}
            // oxlint-disable-next-line jsx-a11y/no-autofocus -- opt-in prop forwarded to the preset trigger; only set when the picker is why the sheet opened
            autoFocus={autoFocus}
            className={cn(
              'relative h-9 min-w-50 justify-start text-left font-normal tabular-nums',
              !value && 'text-muted-foreground'
            )}
          >
            <div className='flex min-w-0 flex-1 items-center gap-2'>
              <DurationReadout
                value={value}
                units={visibleUnits}
                translations={translations}
                placeholder={placeholder || translations.pickADuration}
              />
            </div>
            <NoddiIcon name='Clock' size='small' className='ml-auto shrink-0 text-muted-foreground' />
          </Button>
        </PopoverTrigger>

        <PopoverContent className='w-auto min-w-70 p-0' align='start'>
          <div className='relative flex flex-col gap-3 p-4'>
            {/* Close button */}
            <button
              type='button'
              onClick={() => setOpen(false)}
              aria-label={translations.close}
              className='absolute top-2 right-2 flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
            >
              <NoddiIcon name='X' size={14} />
            </button>

            {/* Quick pick presets */}
            {presets && presets.length > 0 && (
              <div>
                <p className='mb-2 block text-xs font-semibold tracking-wider text-muted-foreground uppercase'>
                  {translations.quickPick}
                </p>
                <div className='flex flex-wrap gap-1.5'>
                  {presets.map((p) => {
                    const selected = p.seconds === value;
                    return (
                      <Button
                        key={p.label}
                        type='button'
                        size='sm'
                        variant={selected ? 'default' : 'outline'}
                        onClick={() => set(p.seconds)}
                        className='h-7 text-xs tabular-nums'
                      >
                        {p.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom unit inputs */}
            <div>
              {presets && presets.length > 0 && (
                <p className='mb-2 block text-xs font-semibold tracking-wider text-muted-foreground uppercase'>
                  {translations.custom}
                </p>
              )}
              <div className='flex flex-col gap-2'>
                {visibleUnits.map((u, i) => {
                  const unitInputId = `${triggerId}-${u}`;
                  const unitLabelId = `${unitInputId}_label`;
                  return (
                    <div key={u} className='flex items-center gap-3'>
                      <DurationUnitStepper
                        id={unitInputId}
                        value={reduced[u]}
                        onChange={(n) => {
                          const next = { ...reduced, [u]: n };
                          set(buildDurationSeconds(next, visibleUnits));
                        }}
                        // oxlint-disable-next-line jsx-a11y/no-autofocus -- first unit stepper of a picker the user just opened, and only when there are no presets to choose first
                        autoFocus={i === 0 && !presets}
                      />
                      <label id={unitLabelId} htmlFor={unitInputId} className='text-sm'>
                        {translations[UNIT_LONG_LABELS[u]]}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className='flex justify-end border-t border-border pt-3'>
              <Button type='button' size='sm' onClick={() => setOpen(false)}>
                {translations.done}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
